import {
  VegaCard,
  VegaChip,
  VegaFlex,
  VegaFont,
  VegaPageNotification,
  VegaPagination,
  VegaTable,
} from '@globalpayments/vega-react';
import type {
  VegaTableColumnConfig,
  VegaTableDataSourceItem,
  VegaTableRowClickPropType,
} from '@globalpayments/vega/dist/types/components/vega-table/interface';
import { getDetailsIdentifier } from '../../../api/kirbyFiltering';
import PageSkeleton from '../../../components/pageSkeleton';
import { KirbyDetailsIdentifier, KirbyFilterResultPage } from '../../../types/kirbyFiltering';
import styles from '../index.module.css';

interface FilterResultsProps {
  data?: KirbyFilterResultPage;
  filterType: string;
  hasSubmitted: boolean;
  isError: boolean;
  isLoading: boolean;
  onPageChange: (pageNumber: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onRequestDetails?: (id: KirbyDetailsIdentifier) => void;
}

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

interface FilterResultTableRecord extends VegaTableDataSourceItem {
  __detailsId?: KirbyDetailsIdentifier;
  __rowKey: string;
}

const createTableData = (filterType: string, records: Record<string, unknown>[]): FilterResultTableRecord[] =>
  records.map((record, index) => ({
    ...Object.fromEntries(Object.entries(record).map(([key, value]) => [key, formatValue(value)])),
    __detailsId: getDetailsIdentifier(filterType, record),
    __rowKey: String(index),
  }));

/** Renders heterogeneous Kirby result records without interpreting their domain fields. */
const FilterResults = ({
  data,
  filterType,
  hasSubmitted,
  isError,
  isLoading,
  onPageChange,
  onPageSizeChange,
  onRequestDetails,
}: FilterResultsProps) => {
  if (isLoading) return <PageSkeleton label="Loading filter results" variant="results" />;
  if (isError) {
    return (
      <VegaPageNotification
        duration={0}
        message="The filter request could not be completed. Review the query and try again."
        notificationTitle="Query failed"
        type="error"
      />
    );
  }
  if (!hasSubmitted) return null;
  if (!data) {
    return (
      <VegaPageNotification
        duration={0}
        message="Kirby returned a result shape that this first pass cannot display."
        notificationTitle="Unsupported result"
        type="warning"
      />
    );
  }

  const { pageNumber, pageSize, results: records, totalCount } = data;
  const propertyNames = [...new Set(records.flatMap((record) => Object.keys(record)))];
  const columns: VegaTableColumnConfig[] = propertyNames.map((property) => ({
    key: property,
    label: property,
    prop: property,
    overflow: 'ellipsis',
    ellipsisOptions: { showTooltip: true },
    width: '220px',
  }));
  const canRequestDetails =
    Boolean(onRequestDetails) && records.some((record) => getDetailsIdentifier(filterType, record) !== undefined);
  const handleRowClick = (event: CustomEvent<VegaTableRowClickPropType>) => {
    const detailsId = (event.detail.record as FilterResultTableRecord).__detailsId;
    if (detailsId !== undefined) onRequestDetails?.(detailsId);
  };

  return (
    <VegaCard padding={{ default: 'size-16', M: 'size-24' }} variant="border">
      <VegaFlex direction="col" gap="size-16">
        <VegaFlex alignItems="center" flexWrap="wrap" gap="size-8" justifyContent="space-between">
          <VegaFont as="h2" color="text-secondary" variant="font-h5">
            Results
          </VegaFont>
          <VegaChip
            chipType="label"
            size="small"
            text={`${records.length} of ${totalCount} ${totalCount === 1 ? 'record' : 'records'}`}
            variant="outlined"
          />
        </VegaFlex>
        {records.length === 0 ? (
          <VegaPageNotification
            duration={0}
            message="No records matched the submitted clauses."
            notificationTitle="No results"
            type="info"
          />
        ) : (
          <div className={styles.resultsTable}>
            <VegaTable
              aria-label="Filtering results"
              columns={columns}
              dataSource={createTableData(filterType, records)}
              density="compact"
              onVegaRowClick={canRequestDetails ? handleRowClick : undefined}
              rowClickable={canRequestDetails}
              rowDivider
              rowKey="__rowKey"
              striped
            />
          </div>
        )}
        <VegaPagination
          aria-label="Filtering results pages"
          current={pageNumber}
          extensions={[
            ['currentPageLabel', 'pageSizeSelector'],
            ['pageSelector', 'goTo'],
          ]}
          onVegaChange={(event) => onPageChange(event.detail)}
          onVegaPageSizeChange={(event) => onPageSizeChange(event.detail)}
          pageSize={pageSize}
          pageSizeSelectorOptions={[20, 50, 100]}
          total={totalCount}
        />
      </VegaFlex>
    </VegaCard>
  );
};

export default FilterResults;
